import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { ArrowLeft, Sparkles, Check, Download, BookOpen, Heart, Printer, Target, Video, Users, Edit, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { PDF_URL } from '../constants/appConfig';
import { FourChoiceExercise, PartnerTimedExercise, TranslationExercise, ObsEvalExercise, DoubleOFNRExercise } from './ExerciseTypes';
import expandedChaptersData from '../data/chapters_expanded.json';

function ChapterView({ chapter, onBack }) {
  const [responses, setResponses] = useState({});
  const [aiScenario, setAiScenario] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState({});
  const [aiEnabled, setAiEnabled] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [analyzingExercise, setAnalyzingExercise] = useState(null);
  // Print current chapter
  const printChapter = () => {
    const printContent = `
      <html>
      <head>
        <title>Chapter ${chapter.id}: ${chapter.title} - The Belonging Sessions</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; color: #333; }
          h1 { color: #2563eb; border-bottom: 3px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #dc2626; margin-top: 30px; }
          h3 { color: #059669; margin-top: 20px; }
          .section { margin: 20px 0; padding: 15px; border-left: 4px solid #ccc; background: #f9f9f9; }
          .exercise { background: #e3f2fd; padding: 15px; margin: 15px 0; border: 1px solid #90caf9; }
          ul { padding-left: 20px; }
          li { margin: 5px 0; }
          @page { margin: 1in; }
        </style>
      </head>
      <body>
        <h1>Chapter ${chapter.id}: ${chapter.title}</h1>
        <p><strong>Pages ${chapter.pages}</strong></p>
        <p><em>${chapter.description}</em></p>
        <p><em>Printed: ${new Date().toLocaleString()}</em></p>
        
        ${expandedChapter && expandedChapter.content ? `
          <h2>Teaching Content</h2>
          ${expandedChapter.content.sections ? expandedChapter.content.sections.map((section, i) => `
            <div class="section">
              <h3>${section.title}</h3>
              ${section.page ? `<p><em>Page ${section.page}</em></p>` : ''}
              ${section.bullets ? `<ul>${section.bullets.map(b => `<li>${b}</li>`).join('')}</ul>` : ''}
              ${section.text ? `<p>${section.text}</p>` : ''}
            </div>
          `).join('') : ''}
          
          ${expandedChapter.content.exercises ? `
            <h2>Exercises</h2>
            ${expandedChapter.content.exercises.map((ex, i) => `
              <div class="exercise">
                <h3>${ex.title}</h3>
                <p><strong>Instructions:</strong> ${ex.instructions || ex.type}</p>
                ${ex.scenarios ? `<p><strong>Scenarios:</strong></p><ul>${ex.scenarios.map(s => `<li>${s}</li>`).join('')}</ul>` : ''}
              </div>
            `).join('')}
          ` : ''}
        ` : ''}
        
        <hr style="margin: 30px 0;">
        <p style="text-align: center; font-size: 12px; color: #666;">
          © 2025 The Belonging Sessions • Steve Torma • Defiance Springs
        </p>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    
    toast.success(`Chapter ${chapter.id} ready for printing!`);
  };

  const [userId] = useState(() => localStorage.getItem('workbook_user_id') || `user-${Date.now()}`);
  const [completedExercises, setCompletedExercises] = useState(new Set());
  const [homeworkAssignments, setHomeworkAssignments] = useState([]);
  
  // Get expanded chapter data
  const expandedChapter = expandedChaptersData.chapters.find(c => c.id === chapter.id);

  useEffect(() => {
    fetchHomeworkAssignments();
  }, [chapter.id]);

  const fetchHomeworkAssignments = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homework/${chapter.id}`);
      if (response.ok) {
        const data = await response.json();
        setHomeworkAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Failed to fetch homework assignments');
    }
  };

  useEffect(() => {
    // Scroll to top when chapter loads
    window.scrollTo({ top: 0, behavior: 'auto' });
    
    localStorage.setItem('workbook_user_id', userId);
    const saved = localStorage.getItem(`chapter-${chapter.id}-responses`);
    if (saved) {
      setResponses(JSON.parse(saved));
    }
    const completed = localStorage.getItem(`chapter-${chapter.id}-completed`);
    if (completed) {
      setCompletedExercises(new Set(JSON.parse(completed)));
    }
    
    // Check if AI features are enabled
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/settings`)
      .then(res => res.json())
      .then(data => setAiEnabled(data.ai_features_enabled || false))
      .catch(() => setAiEnabled(false));
  }, [chapter.id, userId]);

  const saveResponse = (exerciseId, value) => {
    const updated = { ...responses, [exerciseId]: value };
    setResponses(updated);
    localStorage.setItem(`chapter-${chapter.id}-responses`, JSON.stringify(updated));
    
    // Also save to backend database
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/responses`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        chapter_id: chapter.id,
        exercise_id: exerciseId,
        response_text: typeof value === 'string' ? value : JSON.stringify(value)
      })
    }).catch(err => console.error('Save failed:', err));
  };

  const toggleComplete = (exerciseId) => {
    const updated = new Set(completedExercises);
    if (updated.has(exerciseId)) {
      updated.delete(exerciseId);
    } else {
      updated.add(exerciseId);
    }
    setCompletedExercises(updated);
    localStorage.setItem(`chapter-${chapter.id}-completed`, JSON.stringify([...updated]));
    
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/progress`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        chapter_id: chapter.id,
        exercise_id: exerciseId,
        completed: updated.has(exerciseId)
      })
    });
  };

  const generateScenario = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'scenario',
          chapter_id: chapter.id,
          topic: chapter.title
        })
      });
      
      if (!response.ok) throw new Error('Generation failed');
      
      const data = await response.json();
      setAiScenario(data.response);
      toast.success('New practice scenario generated!');
    } catch (error) {
      toast.error('Failed to generate scenario');
    } finally {
      setGenerating(false);
    }
  };

  const analyzeReflection = async (exerciseId) => {
    const userText = responses[exerciseId];
    if (!userText || userText.trim().length < 10) {
      toast.error('Please write a reflection first');
      return;
    }

    setAnalyzingExercise(exerciseId);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workbook/ai-assist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'analyze',
          user_text: userText
        })
      });
      
      if (!response.ok) throw new Error('Analysis failed');
      
      const data = await response.json();
      setAiAnalysis({...aiAnalysis, [exerciseId]: data.response});
      toast.success('M.ai.ry has analyzed your reflection!');
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzingExercise(null);
    }
  };

  const progressPercent = chapter.exercises.length > 0 
    ? (completedExercises.size / chapter.exercises.length) * 100 
    : 0;

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <Button 
            onClick={onBack}
            variant="ghost"
            className="px-0 hover:bg-transparent"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Chapters
          </Button>
          
          <Button 
            onClick={printChapter}
            variant="outline"
            className="border-black"
            style={{ borderRadius: 0 }}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Chapter
          </Button>
        </div>

        <div className="mb-12">
          <p className="text-sm text-gray-600 mb-2">Chapter {chapter.id} • Pages {chapter.pages}</p>
          
          {chapter.image_url && (
            <div className="mb-8">
              <img 
                src={chapter.image_url} 
                alt={chapter.title}
                className="w-full h-64 object-cover border-2 border-black"
              />
            </div>
          )}
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6">{chapter.title}</h1>
          <p className="text-2xl text-gray-700 mb-8">{chapter.description}</p>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-semibold">Chapter Progress</span>
              <span>{completedExercises.size} of {chapter.exercises.length} exercises</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
        </div>

        <div className="mb-12 p-8 bg-gray-50">
          <h3 className="text-2xl font-bold mb-4">Key Topics</h3>
          <div className="flex flex-wrap gap-3">
            {chapter.keyTopics.map((topic, i) => (
              <span key={i} className="px-4 py-2 bg-black text-white text-sm font-semibold">
                {topic}
              </span>
            ))}
          </div>
        </div>

        {/* Expanded Content Sections */}
        {expandedChapter && expandedChapter.content && (
          <div className="mb-12 space-y-6">
            <h3 className="text-2xl font-bold">Chapter Content</h3>
            
            {expandedChapter.content.sections && expandedChapter.content.sections.map((section, i) => (
              <Card key={i} style={{ borderRadius: 0 }} className="border-2 border-gray-400">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    {section.title}
                  </CardTitle>
                  {section.page && <p className="text-sm text-gray-600">Page {section.page}</p>}
                  {section.author && <p className="text-sm text-purple-600">By {section.author}</p>}
                </CardHeader>
                <CardContent>
                  {section.bullets && (
                    <ul className="space-y-2 list-disc list-inside">
                      {section.bullets.map((bullet, j) => (
                        <li key={j} className="text-gray-700">{bullet}</li>
                      ))}
                    </ul>
                  )}
                  {section.type === 'wisdom_card' && (
                    <div className="p-4 bg-blue-50 border-l-4 border-blue-600">
                      <Heart className="w-6 h-6 text-blue-600 mb-2" />
                      <p className="text-gray-700 leading-relaxed italic">{section.text}</p>
                    </div>
                  )}
                  {section.type === 'fears_exploration' && section.fears && (
                    <div className="space-y-2">
                      {section.fears.map((fear, k) => (
                        <div key={k} className="p-3 bg-gray-50 border-l-2 border-gray-400">
                          <p className="text-sm text-gray-700">{fear}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}


        {/* AI Practice Scenario - Hidden by default */}
        {aiEnabled && (
          <Card className="mb-12" style={{ borderRadius: 0 }}>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-3xl">AI Practice Scenario</CardTitle>
                <Button 
                  onClick={generateScenario}
                  disabled={generating}
                  className="bg-black text-white hover:bg-black/90"
                  style={{ borderRadius: 0 }}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {generating ? 'Generating...' : 'Generate New Scenario'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {aiScenario ? (
                <div className="p-6 bg-gray-50">
                  <p className="text-xl leading-relaxed">{aiScenario}</p>
                </div>
              ) : (
                <p className="text-gray-600">Click the button to generate a custom practice scenario for this chapter.</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="space-y-8">
          <h2 className="text-4xl font-bold">Exercises</h2>
          {chapter.exercises.map((exercise, idx) => {
            const exerciseKey = exercise.id || `exercise-${idx}`;
            
            return (
              <Card key={idx} style={{ borderRadius: 0 }}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-2xl mb-3">{exercise.title}</CardTitle>
                      <p className="text-lg text-gray-700">{exercise.instructions}</p>
                      {exercise.example && (
                        <div className="mt-3 p-3 bg-gray-50 border-l-4 border-black">
                          <p className="text-sm font-semibold mb-1">Example:</p>
                          <p className="text-sm">{exercise.example}</p>
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => toggleComplete(exerciseKey)}
                      variant={completedExercises.has(exerciseKey) ? 'default' : 'outline'}
                      className={completedExercises.has(exerciseKey) ? 'bg-black text-white' : 'border-black'}
                      style={{ borderRadius: 0 }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {completedExercises.has(exerciseKey) ? 'Completed' : 'Mark Complete'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Render specialized component based on exercise type */}
                  {exercise.type === 'four_choice' && (
                    <FourChoiceExercise 
                      exercise={exercise}
                      onSave={(data) => saveResponse(exerciseKey, JSON.stringify(data))}
                      savedResponse={responses[exerciseKey] ? JSON.parse(responses[exerciseKey] || '{}') : {}}
                    />
                  )}
                  
                  {exercise.type === 'double_ofnr' && (
                    <DoubleOFNRExercise 
                      exercise={exercise}
                      onSave={(data) => saveResponse(exerciseKey, JSON.stringify(data))}
                      savedResponse={responses[exerciseKey] ? JSON.parse(responses[exerciseKey] || '{}') : {}}
                    />
                  )}
                  
                  {exercise.type === 'translation' && exercise.items && (
                    <TranslationExercise 
                      exercise={exercise}
                      onSave={(data) => saveResponse(exerciseKey, JSON.stringify(data))}
                      savedResponse={responses[exerciseKey] ? JSON.parse(responses[exerciseKey] || '{}') : {}}
                    />
                  )}
                  
                  {exercise.type === 'obs_eval' && (
                    <ObsEvalExercise 
                      exercise={exercise}
                      onSave={(data) => saveResponse(exerciseKey, JSON.stringify(data))}
                      savedResponse={responses[exerciseKey] ? JSON.parse(responses[exerciseKey] || '{}') : {}}
                    />
                  )}
                  
                  {exercise.type === 'partner_timed' && (
                    <PartnerTimedExercise 
                      exercise={exercise}
                      onSave={(data) => saveResponse(exerciseKey, data)}
                      savedResponse={responses[exerciseKey] || ''}
                    />
                  )}
                  
                  {/* Default textarea for other types */}
                  {!['four_choice', 'double_ofnr', 'translation', 'obs_eval', 'partner_timed'].includes(exercise.type) && (
                    <div>
                      <Textarea
                        placeholder="Write your reflections here..."
                        value={responses[exerciseKey] || ''}
                        onChange={(e) => saveResponse(exerciseKey, e.target.value)}
                        className="min-h-[200px] text-lg border-black"
                        style={{ borderRadius: 0 }}
                      />
                      <p className="text-sm text-gray-600 mt-2">✓ Auto-saved to your browser</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Home Practice - Always Visible Section */}
        <Card className="mb-12 border-4 border-orange-500 bg-orange-50" style={{ borderRadius: 0 }}>
          <CardHeader>
            <CardTitle className="text-3xl flex items-center gap-3">
              <Target className="w-8 h-8 text-orange-600" />
              🏠 Home Practice for Chapter {chapter.id}
            </CardTitle>
            <p className="text-xl text-orange-700 font-semibold">
              {homeworkAssignments.length > 0 
                ? `${homeworkAssignments.length} assignments from your instructors`
                : 'Standard practice assignments (instructors can add more via Admin)'}
            </p>
          </CardHeader>
          <CardContent className="p-8">
            
            {/* Priority: Admin-Created Assignments */}
            {homeworkAssignments.length > 0 && (
              <div className="space-y-6 mb-8">
                <div className="bg-green-50 border-4 border-green-500 p-4 text-center" style={{ borderRadius: 0 }}>
                  <h3 className="text-xl font-bold text-green-800 mb-2">
                    📋 Current Assignments from Your Instructors
                  </h3>
                  <p className="text-green-700">Complete these before the next session</p>
                </div>

                {homeworkAssignments.map((assignment, index) => (
                  <Card key={assignment.id} className="border-4 border-orange-400 bg-white" style={{ borderRadius: 0 }}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-2xl text-orange-800 flex items-center gap-2">
                            {assignment.assignment_type === 'reading' && <BookOpen className="w-6 h-6" />}
                            {assignment.assignment_type === 'video' && <Video className="w-6 h-6" />}
                            {assignment.assignment_type === 'partner' && <Users className="w-6 h-6" />}
                            {assignment.assignment_type === 'practice' && <Target className="w-6 h-6" />}
                            {assignment.assignment_type === 'exercise' && <Edit className="w-6 h-6" />}
                            Assignment {index + 1}: {assignment.title}
                          </CardTitle>
                          {assignment.description && (
                            <p className="text-lg text-orange-700">{assignment.description}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {assignment.required && (
                            <Badge className="bg-red-500 text-white text-base">Required</Badge>
                          )}
                          {assignment.estimated_time && (
                            <Badge variant="outline" className="border-orange-500 text-base">
                              <Clock className="w-4 h-4 mr-1" />
                              {assignment.estimated_time}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        
                        {/* Assignment Content - Large and Clear */}
                        <div className="p-6 bg-orange-25 border-l-8 border-orange-500 text-lg leading-relaxed">
                          <div className="whitespace-pre-wrap text-gray-800">{assignment.content}</div>
                          {assignment.page_reference && (
                            <p className="text-base text-orange-600 mt-4 font-semibold">
                              📖 Reference: {assignment.page_reference}
                            </p>
                          )}
                        </div>

                        {/* Instructions */}
                        {assignment.instructions && (
                          <div className="p-4 bg-blue-50 border-l-4 border-blue-500">
                            <h5 className="font-bold text-blue-800 mb-2 text-lg">👨‍🏫 Instructions:</h5>
                            <p className="text-base text-blue-700">{assignment.instructions}</p>
                          </div>
                        )}

                        {/* Large Completion Section */}
                        <div className="p-6 bg-white border-4 border-orange-500" style={{ borderRadius: 0 }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <input 
                                type="checkbox" 
                                className="w-6 h-6 cursor-pointer"
                                id={`homework-${assignment.id}`}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    toast.success(`${assignment.title} completed! Excellent work! 🎉`, { duration: 5000 });
                                    localStorage.setItem(`homework-${assignment.id}`, 'completed');
                                  }
                                }}
                                defaultChecked={localStorage.getItem(`homework-${assignment.id}`) === 'completed'}
                              />
                              <label htmlFor={`homework-${assignment.id}`} className="text-xl font-semibold text-gray-800 cursor-pointer">
                                Mark Assignment Complete
                              </label>
                            </div>
                            <Button
                              className="bg-orange-600 text-white hover:bg-orange-700 text-lg px-6 py-3"
                              style={{ borderRadius: 0 }}
                              onClick={() => {
                                // Print this assignment with better formatting
                                const printContent = `
                                  <html>
                                  <head>
                                    <title>${assignment.title} - Chapter ${chapter.id}</title>
                                    <style>
                                      body { font-family: 'Georgia', serif; margin: 40px; line-height: 1.8; color: #333; }
                                      h1 { color: #ea580c; border-bottom: 3px solid #ea580c; padding-bottom: 15px; }
                                      h2 { color: #dc2626; margin-top: 30px; }
                                      .content { 
                                        white-space: pre-wrap; 
                                        margin: 30px 0; 
                                        padding: 20px; 
                                        background: #fef7ed; 
                                        border-left: 6px solid #ea580c; 
                                        font-size: 16px;
                                        line-height: 1.8;
                                      }
                                      .instructions { 
                                        background: #eff6ff; 
                                        padding: 20px; 
                                        border-left: 6px solid #3b82f6; 
                                        margin: 20px 0;
                                      }
                                      @page { margin: 1.5in; }
                                    </style>
                                  </head>
                                  <body>
                                    <h1>${assignment.title}</h1>
                                    <p><strong>Chapter ${chapter.id} - ${assignment.assignment_type.charAt(0).toUpperCase() + assignment.assignment_type.slice(1)} Assignment</strong></p>
                                    ${assignment.page_reference ? `<p><em>Reference: ${assignment.page_reference}</em></p>` : ''}
                                    ${assignment.estimated_time ? `<p><em>Estimated Time: ${assignment.estimated_time}</em></p>` : ''}
                                    
                                    <h2>Assignment Content:</h2>
                                    <div class="content">${assignment.content}</div>
                                    
                                    ${assignment.instructions ? `
                                      <div class="instructions">
                                        <h2>Instructions:</h2>
                                        ${assignment.instructions}
                                      </div>
                                    ` : ''}
                                    
                                    <hr style="margin: 40px 0; border: 1px solid #ea580c;">
                                    <p style="text-align: center; font-size: 14px; color: #666;">
                                      © 2025 The Belonging Sessions - Chapter ${chapter.id} Home Practice<br>
                                      Steve Torma • Defiance Springs
                                    </p>
                                  </body>
                                  </html>
                                `;
                                const printWindow = window.open('', '_blank');
                                printWindow.document.write(printContent);
                                printWindow.document.close();
                                printWindow.print();
                                toast.success('Assignment ready for printing!');
                              }}
                            >
                              🖨️ Print Assignment
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Fallback or Additional Static Assignments */}
            {expandedChapter && expandedChapter.content && expandedChapter.content.homework && (
              <div className="space-y-6">
                <div className="bg-blue-50 border-4 border-blue-500 p-4 text-center" style={{ borderRadius: 0 }}>
                  <h3 className="text-xl font-bold text-blue-800 mb-2">
                    📚 Additional Practice Resources
                  </h3>
                  <p className="text-blue-700">Standard assignments to supplement instructor-created content</p>
                </div>
                
                {/* Reading Assignments */}
                {expandedChapter.content.homework.reading && (
                  <div>
                    <h4 className="text-lg font-bold text-orange-800 mb-3">📚 Reading Assignments</h4>
                    <div className="space-y-2">
                      {expandedChapter.content.homework.reading.map((reading, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-white border-l-4 border-orange-500">
                          <input 
                            type="checkbox" 
                            className="mt-1"
                            onChange={(e) => {
                              if (e.target.checked) {
                                toast.success('Reading assignment completed! 📖');
                              }
                            }}
                          />
                          <p className="text-gray-700">{reading}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video Assignments */}
                {expandedChapter.content.homework.video && (
                  <div>
                    <h4 className="text-lg font-bold text-orange-800 mb-3">🎥 Video Practice</h4>
                    <div className="p-4 bg-white border-l-4 border-orange-500">
                      <div className="flex items-start gap-3">
                        <input 
                          type="checkbox" 
                          className="mt-1"
                          onChange={(e) => {
                            if (e.target.checked) {
                              toast.success('Video practice completed! 🎬');
                            }
                          }}
                        />
                        <p className="text-gray-700">{expandedChapter.content.homework.video}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Partner Exercises */}
                {expandedChapter.content.homework.partner_exercises && (
                  <div>
                    <h4 className="text-lg font-bold text-orange-800 mb-3">👥 Partner Practice</h4>
                    <div className="space-y-3">
                      {expandedChapter.content.homework.partner_exercises.map((exercise, i) => (
                        <div key={i} className="p-4 bg-white border-l-4 border-orange-500">
                          <div className="flex items-start gap-3 mb-2">
                            <input 
                              type="checkbox" 
                              className="mt-1"
                              onChange={(e) => {
                                if (e.target.checked) {
                                  toast.success(`Partner exercise "${exercise.title}" completed! 🤝`);
                                }
                              }}
                            />
                            <div>
                              <h5 className="font-bold text-gray-800">{exercise.title}</h5>
                              <p className="text-sm text-gray-600">{exercise.instructions}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Practice Assignments */}
                {expandedChapter.content.homework.practice && (
                  <div>
                    <h4 className="text-lg font-bold text-orange-800 mb-3">✨ Daily Practice</h4>
                    <div className="p-4 bg-white border-l-4 border-orange-500">
                      <div className="flex items-start gap-3">
                        <input 
                          type="checkbox" 
                          className="mt-1"
                          onChange={(e) => {
                            if (e.target.checked) {
                              toast.success('Daily practice completed! ⭐');
                            }
                          }}
                        />
                        <p className="text-gray-700">{expandedChapter.content.homework.practice}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructor Assignment Note */}
                <Card className="bg-green-50 border-2 border-green-500" style={{ borderRadius: 0 }}>
                  <CardContent className="p-4 text-center">
                    <p className="text-green-800 font-semibold mb-2">
                      📊 Chapter {chapter.id} Home Practice Progress
                    </p>
                    <p className="text-sm text-green-700 mb-3">
                      {homeworkAssignments.length > 0 
                        ? 'Complete these assignments to deepen your understanding before the next session.'
                        : 'These assignments are designed to deepen your practice between sessions. Complete them at your own pace and bring insights to our next gathering.'}
                    </p>
                    <Button
                      className="bg-green-600 text-white hover:bg-green-700"
                      style={{ borderRadius: 0 }}
                      onClick={() => {
                        const userId = localStorage.getItem('workbook_user_id');
                        if (userId) {
                          toast.success('Home practice progress saved to your LORE! 📝');
                        } else {
                          toast.info('Create an account to save your practice progress!');
                        }
                      }}
                    >
                      💾 Save {homeworkAssignments.length > 0 ? 'All' : 'Practice'} Progress
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="mt-12 p-8 bg-black text-white text-center">
          <h3 className="text-3xl font-bold mb-4">Want the Full Detail?</h3>
          <p className="text-xl mb-6">Download the original workbook pages {chapter.pages}</p>
          <a 
            href={PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="chapter-pdf-download"
          >
            <Button className="bg-white text-black hover:bg-white/90 px-8 py-6 text-lg" style={{ borderRadius: 0 }}>
              <Download className="w-5 h-5 mr-2" />
              Download Belonging eBook PDF
            </Button>
          </a>
        </div>

        {/* Practice Mode - No AI Branding */}
        <div className="mt-12 p-8 rounded-sm" style={{ backgroundColor: 'hsl(138 25% 96%)', border: '2px solid hsl(138 30% 40%)' }}>
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-3">Practice Solo</h3>
            <p className="text-base text-gray-700 mb-6">
              <strong>Priority: Practice with a partner or friend whenever possible.</strong> NVC is best learned through real human connection. 
              Use this tool for additional scenarios when you don't have access to a practice partner.
            </p>
            {!aiScenario ? (
              <Button 
                onClick={generateScenario}
                disabled={generating}
                className="bg-black text-white hover:bg-black/90 px-8 py-6"
                style={{ borderRadius: 0 }}
              >
                {generating ? 'Creating Practice Scenario...' : 'Generate Practice Scenario'}
              </Button>
            ) : (
              <div className="space-y-6">
                <div className="p-6 bg-white border-2 border-black">
                  <p className="text-sm font-semibold mb-2">Practice Scenario:</p>
                  <p className="text-lg leading-relaxed">{aiScenario}</p>
                </div>
                
                <div>
                  <Label className="text-base font-semibold mb-2 block">Your Response (saved to your dashboard):</Label>
                  <Textarea
                    placeholder="How would you respond using NVC? Write your OFNR, identify feelings/needs, or practice whatever this chapter teaches..."
                    value={responses[`practice-solo-${chapter.id}`] || ''}
                    onChange={(e) => saveResponse(`practice-solo-${chapter.id}`, e.target.value)}
                    className="min-h-[150px] text-lg border-black"
                    style={{ borderRadius: 0 }}
                  />
                  <p className="text-sm text-gray-600 mt-2">✓ Auto-saved to your dashboard & journal</p>
                </div>
                
                <div className="flex gap-4">
                  <Button 
                    onClick={generateScenario}
                    disabled={generating}
                    variant="outline"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  >
                    Generate Another
                  </Button>
                  <Button 
                    onClick={() => setAiScenario('')}
                    variant="outline"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  >
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChapterView;
